import type { UpdateSalesFeedbackFormData } from "src/types/feedback";
import {
  notEmpty,
  validName,
  validEmail,
  validate,
  test,
  type FormErrorShape,
  type TestResultShape,
  type ValidationMapperFunc,
} from "src/utils";

export const SALES_REP_OPTION_OTHER = "__OTHER__";
export const SALES_REP_OPTION_NOT_SURE = "__NOT_SURE__";

export type SalesPersonValidationContext =
  | { mode: "manual" }
  | { mode: "dropdown"; selectedOption: string };

export type SalesPersonFormErrors =
  FormErrorShape<UpdateSalesFeedbackFormData> & {
    sales_rep_selection?: TestResultShape;
  };

const manualValidationMapper =
  (): ValidationMapperFunc<UpdateSalesFeedbackFormData> => {
    return () => ({
      sales_rep_name: [
        test(notEmpty, "Must not be empty"),
        test(validName, "Only letters, dashes, and spaces allowed"),
      ],
      sales_rep_email: [test(validEmail, "Must be a valid email")],
    });
  };

const otherValidationMapper =
  (): ValidationMapperFunc<UpdateSalesFeedbackFormData> => {
    return () => ({
      sales_rep_name: [
        test(notEmpty, "Must not be empty"),
        test(validName, "Only letters, dashes, and spaces allowed"),
      ],
      sales_rep_email: [
        test(notEmpty, "Must not be empty"),
        test((val) => Boolean(val && validEmail(val)), "Must be a valid email"),
      ],
    });
  };

export const getSalesPersonFormErrors = (
  formData: UpdateSalesFeedbackFormData,
  context: SalesPersonValidationContext = { mode: "manual" },
): SalesPersonFormErrors => {
  if (context.mode === "dropdown") {
    if (!context.selectedOption) {
      return {
        sales_rep_selection: {
          message: "Must select a sales representative",
          blockSubmit: true,
        },
      };
    }

    if (context.selectedOption === SALES_REP_OPTION_NOT_SURE) {
      return {};
    }

    if (context.selectedOption === SALES_REP_OPTION_OTHER) {
      return validate(formData, otherValidationMapper());
    }

    return {};
  }

  return validate(formData, manualValidationMapper());
};
